import { type NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { nip19 } from 'nostr-tools';

/** Marc's pubkey hex */
export const MARC_PUBKEY = 'df478568479de26b4a83c1bdc4dbab61b5cc82e1a312e2b28bc815a12a951e67';

const PAGE_SIZE = 50;

/**
 * Curated fallback set of major relays. Used to *discover* Marc's NIP-65 relay
 * list (kind 10002) and as a baseline that we always merge with whatever relays
 * he actually publishes to. This guarantees coverage even if his relay list is
 * missing, sparse, or unreachable.
 *
 * Note: `wss://cache2.primal.net/v1` is Primal's caching/API service (a custom
 * protocol), not a standard queryable relay — its events mirror to
 * `wss://relay.primal.net/`, which we query instead.
 */
const FALLBACK_RELAYS = [
  'wss://relay.primal.net/',
  'wss://relay.nostr.band/',
  'wss://nos.lol/',
  'wss://relay.damus.io/',
  'wss://nostr.mom/',
  'wss://relay.snort.social/',
  'wss://nostr.wine/',
  'wss://purplepag.es/',
  'wss://relay.nostr.bg/',
  'wss://relay.ditto.pub/',
  'wss://relay.dreamith.to/',
];

/** Normalize a relay URL to a canonical form (trailing slash, lowercase host). */
function normalizeRelayUrl(url: string): string | null {
  try {
    const u = new URL(url.trim());
    if (u.protocol !== 'wss:' && u.protocol !== 'ws:') return null;
    // Ensure a trailing slash on the path root for consistent dedup.
    let s = u.toString();
    if (!s.endsWith('/') && u.pathname === '/') s += '/';
    return s;
  } catch {
    return null;
  }
}

/**
 * Fetch Marc's NIP-65 relay list (kind 10002) from the fallback relays, then
 * merge his declared relays (read + write) with the fallback set. The result
 * is the relay list the gallery queries — so it automatically follows wherever
 * Marc chooses to publish, with the fallbacks guaranteeing baseline coverage.
 */
export function useGalleryRelays(): string[] {
  const { nostr } = useNostr();

  const { data } = useQuery<string[]>({
    queryKey: ['gallery-relays', MARC_PUBKEY],
    queryFn: async ({ signal }) => {
      const discovery = nostr.group(FALLBACK_RELAYS);
      const events = await discovery.query(
        [{ kinds: [10002], authors: [MARC_PUBKEY], limit: 1 }],
        { signal: signal ?? AbortSignal.timeout(6000) },
      );

      // Latest kind 10002 wins.
      const relayList = events.sort((a, b) => b.created_at - a.created_at)[0];
      const declared: string[] = [];
      if (relayList) {
        for (const tag of relayList.tags) {
          // ["r", "wss://relay", ("read"|"write")?]  — include all (read+write)
          if (tag[0] === 'r' && typeof tag[1] === 'string') {
            const norm = normalizeRelayUrl(tag[1]);
            if (norm) declared.push(norm);
          }
        }
      }

      return declared;
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  // Merge declared relays with fallbacks, deduplicated.
  return useMemo(() => {
    const set = new Set<string>();
    for (const r of FALLBACK_RELAYS) {
      const norm = normalizeRelayUrl(r);
      if (norm) set.add(norm);
    }
    for (const r of data ?? []) set.add(r);
    return [...set];
  }, [data]);
}

export interface ArtPhoto {
  event: NostrEvent;
  /** Primary image URL */
  url: string;
  /** Alt text from imeta, or empty string */
  alt: string;
  /** Dimensions string e.g. "1920x1080", may be undefined */
  dim?: string;
  /** BlurHash for progressive loading, may be undefined */
  blurhash?: string;
  /** Caption / description */
  caption: string;
  /** Event title if present */
  title?: string;
}

/** Extract image URL entries from an imeta tag array like ["imeta", "url https://...", "m image/jpeg", ...] */
function parseImeta(tag: string[]): { url?: string; alt?: string; dim?: string; blurhash?: string } {
  const result: { url?: string; alt?: string; dim?: string; blurhash?: string } = {};
  for (let i = 1; i < tag.length; i++) {
    const part = tag[i];
    if (part.startsWith('url ')) result.url = part.slice(4).trim();
    else if (part.startsWith('alt ')) result.alt = part.slice(4).trim();
    else if (part.startsWith('dim ')) result.dim = part.slice(4).trim();
    else if (part.startsWith('blurhash ')) result.blurhash = part.slice(9).trim();
  }
  return result;
}

/** Extract all image URLs from a kind-1 note's content */
const IMAGE_URL_REGEX = /https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|avif|apng)(?:[?#]\S*)?/gi;

function extractImagesFromContent(content: string): string[] {
  return [...content.matchAll(new RegExp(IMAGE_URL_REGEX.source, 'gi'))].map((m) => m[0]);
}

/**
 * Returns true if the event is tagged with #artstr — either via a proper
 * `t` tag (any casing) or by the hashtag appearing inline in the content.
 */
export function hasArtstrTag(event: NostrEvent): boolean {
  const hasTag = event.tags.some(
    (t) => t[0] === 't' && typeof t[1] === 'string' && t[1].toLowerCase() === 'artstr',
  );
  if (hasTag) return true;
  // Inline hashtag fallback: "#artstr" as a standalone word (case-insensitive)
  return /(?:^|\s)#artstr\b/i.test(event.content);
}

/**
 * Collect the event IDs that an event references (quote-reposts / mentions):
 *  - `e` and `q` tags
 *  - `nostr:nevent1…` / `nostr:note1…` identifiers in the content
 * Returns lowercase hex ids.
 */
export function extractReferencedEventIds(event: NostrEvent): string[] {
  const ids = new Set<string>();

  // From tags: e (mention/reply) and q (quote, NIP-18)
  for (const tag of event.tags) {
    if ((tag[0] === 'e' || tag[0] === 'q') && typeof tag[1] === 'string' && /^[0-9a-f]{64}$/i.test(tag[1])) {
      ids.add(tag[1].toLowerCase());
    }
  }

  // From content: nostr:nevent1… and nostr:note1…
  const matches = event.content.matchAll(/nostr:(nevent1[0-9a-z]+|note1[0-9a-z]+)/gi);
  for (const m of matches) {
    try {
      const decoded = nip19.decode(m[1]);
      if (decoded.type === 'note') {
        ids.add(decoded.data.toLowerCase());
      } else if (decoded.type === 'nevent') {
        ids.add(decoded.data.id.toLowerCase());
      }
    } catch {
      // ignore malformed identifiers
    }
  }

  return [...ids];
}

/**
 * Extract image entries directly contained in an event (imeta tags for kind 20,
 * inline image URLs for kind 1). Does NOT follow quote-reposts.
 */
function ownImages(event: NostrEvent): Array<{ url: string; alt: string; dim?: string; blurhash?: string; title?: string }> {
  const out: Array<{ url: string; alt: string; dim?: string; blurhash?: string; title?: string }> = [];

  if (event.kind === 20) {
    const imetaTags = event.tags.filter((t) => t[0] === 'imeta');
    const title = event.tags.find((t) => t[0] === 'title')?.[1];
    for (const tag of imetaTags) {
      const { url, alt, dim, blurhash } = parseImeta(tag);
      if (url) out.push({ url, alt: alt ?? '', dim, blurhash, title });
    }
  } else {
    // kind 1 (and any other): scan content for image URLs
    for (const url of extractImagesFromContent(event.content)) {
      out.push({ url, alt: '' });
    }
  }

  return out;
}

/**
 * Convert a single raw Nostr event into zero or more ArtPhoto objects.
 *
 * If `resolved` is provided, quote-reposts (notes that reference another event
 * but contain no image themselves) are followed: the image is pulled from the
 * referenced event while the caption/attribution stays on the original #artstr
 * post.
 */
export function eventToPhotos(
  event: NostrEvent,
  resolved?: Map<string, NostrEvent>,
): ArtPhoto[] {
  const photos: ArtPhoto[] = [];
  const captionBase = event.content.replace(new RegExp(IMAGE_URL_REGEX.source, 'gi'), '');
  // Strip nostr: mentions from the caption so quote text reads cleanly.
  const caption = captionBase.replace(/nostr:(nevent1[0-9a-z]+|note1[0-9a-z]+|npub1[0-9a-z]+|nprofile1[0-9a-z]+)/gi, '').trim();

  // 1. Images that live directly in this event.
  const direct = ownImages(event);
  for (const img of direct) {
    photos.push({
      event,
      url: img.url,
      alt: img.alt,
      dim: img.dim,
      blurhash: img.blurhash,
      caption: event.kind === 20 ? event.content : caption,
      title: img.title,
    });
  }

  // 2. If this event has no image of its own but quotes other events, pull
  //    images from the resolved quoted events (quote-repost support).
  if (direct.length === 0 && resolved) {
    for (const refId of extractReferencedEventIds(event)) {
      const quoted = resolved.get(refId);
      if (!quoted) continue;
      for (const img of ownImages(quoted)) {
        photos.push({
          event,
          url: img.url,
          alt: img.alt,
          dim: img.dim,
          blurhash: img.blurhash,
          caption: caption || quoted.content,
          title: img.title,
        });
      }
    }
  }

  return photos;
}

// ─── Paginated history ────────────────────────────────────────────────────────

interface ArtstrPage {
  /** #artstr events found on this page (already filtered) */
  events: NostrEvent[];
  /** Resolved quoted events, keyed by hex id (for quote-repost images) */
  resolved: Array<[string, NostrEvent]>;
  /** Oldest created_at across ALL fetched events (for pagination cursor) */
  oldest: number | null;
  /** Number of raw events fetched before filtering (to detect end of history) */
  rawCount: number;
}

export function useArtstrPhotosInfinite(relays: string[] = []) {
  const { nostr } = useNostr();

  return useInfiniteQuery<ArtstrPage>({
    // Include relays in the key so the feed refetches if the relay list changes.
    queryKey: ['artstr-photos-infinite', MARC_PUBKEY, relays],
    enabled: relays.length > 0,
    queryFn: async ({ pageParam, signal }) => {
      const until = pageParam as number | undefined;
      const timeout = signal ?? AbortSignal.timeout(10000);

      // Query the dynamic relay group (Marc's NIP-65 relays + fallbacks).
      const group = nostr.group(relays);

      // Two complementary filters, run in a single query:
      //  1. Tag-indexed: fast, relay-side #t filter (common casings).
      //  2. Broad: recent image posts by Marc, scanned client-side for the
      //     hashtag — catches notes that use "#artstr" inline without a t-tag.
      const tagFilter = {
        kinds: [20, 1],
        authors: [MARC_PUBKEY],
        '#t': ['artstr', 'ARTSTR', 'Artstr'],
        limit: PAGE_SIZE,
        ...(until ? { until } : {}),
      };
      const broadFilter = {
        kinds: [20, 1],
        authors: [MARC_PUBKEY],
        limit: PAGE_SIZE,
        ...(until ? { until } : {}),
      };

      const raw = await group.query([tagFilter, broadFilter], { signal: timeout });

      // Dedupe raw events by id.
      const rawById = new Map<string, NostrEvent>();
      for (const ev of raw) rawById.set(ev.id, ev);
      const rawEvents = [...rawById.values()];

      // Oldest timestamp drives pagination (regardless of tag filtering).
      let oldest: number | null = null;
      for (const ev of rawEvents) {
        if (oldest === null || ev.created_at < oldest) oldest = ev.created_at;
      }

      // Keep only events that actually carry the #artstr hashtag.
      const events = rawEvents
        .filter(hasArtstrTag)
        .sort((a, b) => b.created_at - a.created_at);

      // Resolve quote-reposts: for #artstr notes with no image of their own,
      // fetch the events they reference so we can display the quoted image.
      const neededIds = new Set<string>();
      for (const ev of events) {
        if (ownImages(ev).length === 0) {
          for (const id of extractReferencedEventIds(ev)) neededIds.add(id);
        }
      }

      const resolved: Array<[string, NostrEvent]> = [];
      if (neededIds.size > 0) {
        try {
          const quoted = await group.query(
            [{ ids: [...neededIds], kinds: [1, 20] }],
            { signal: timeout },
          );
          for (const ev of quoted) resolved.push([ev.id, ev]);
        } catch {
          // best-effort — missing quoted events just won't render
        }
      }

      return { events, resolved, oldest, rawCount: rawEvents.length };
    },
    getNextPageParam: (lastPage) => {
      // Stop when the broad query returns no more history.
      if (lastPage.rawCount === 0 || lastPage.oldest === null) return undefined;
      return lastPage.oldest - 1;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 2 * 60 * 1000,
    retry: 3,
  });
}

// ─── Live subscription for new events ────────────────────────────────────────

/**
 * Subscribes to real-time #artstr events from Marc. Returns new photos that
 * arrived after `since` (defaults to now at mount time). These are prepended
 * to the feed so the gallery updates without a page refresh.
 */
export function useArtstrLive(since: number, relays: string[] = []) {
  const { nostr } = useNostr();
  const [newPhotos, setNewPhotos] = useState<ArtPhoto[]>([]);
  const seenIds = useRef<Set<string>>(new Set());

  const addPhoto = useCallback((photo: ArtPhoto) => {
    const key = `${photo.event.id}-${photo.url}`;
    if (seenIds.current.has(key)) return;
    seenIds.current.add(key);
    setNewPhotos((prev) => [photo, ...prev]);
  }, []);

  // Stable string key so the effect only re-subscribes when the set changes.
  const relaysKey = (relays ?? []).join(',');

  useEffect(() => {
    if (!relays || relays.length === 0) return;

    const controller = new AbortController();

    (async () => {
      try {
        const group = nostr.group(relays);
        for await (const msg of group.req(
          [
            {
              kinds: [20, 1],
              authors: [MARC_PUBKEY],
              '#t': ['artstr', 'ARTSTR', 'Artstr'],
              since,
            },
          ],
          { signal: controller.signal },
        )) {
          if (msg[0] === 'EVENT') {
            const event = msg[2];
            const direct = eventToPhotos(event);
            if (direct.length > 0) {
              for (const photo of direct) addPhoto(photo);
            } else {
              // Quote-repost: fetch referenced events, then extract their images.
              const refIds = extractReferencedEventIds(event);
              if (refIds.length > 0) {
                try {
                  const quoted = await group.query(
                    [{ ids: refIds, kinds: [1, 20] }],
                    { signal: AbortSignal.timeout(8000) },
                  );
                  const resolved = new Map<string, NostrEvent>(
                    quoted.map((q) => [q.id, q]),
                  );
                  for (const photo of eventToPhotos(event, resolved)) addPhoto(photo);
                } catch {
                  // ignore
                }
              }
            }
          }
        }
      } catch {
        // Subscription closed (normal on unmount)
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nostr, since, addPhoto, relaysKey]);

  return newPhotos;
}
